import React, { useEffect, useMemo, useState } from "react";
import BorderedButton from "../../../Buttons/BorderedButton";
import PrimaryButton from "../../../Buttons/PrimaryButton";
import styles from "./Style.module.css";

const parseAmount = (value) => {
	const amount = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
	return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toFixed(2);

const getGiftTitle = (gift, index) => {
	const fallback = `Gift ${String.fromCharCode(65 + index)}`;
	return gift?.gift_category_title || gift?.category_title || gift?.category_name || fallback;
};

const getGiftName = (gift) =>
	gift?.gift_name || gift?.remarks || gift?.name || gift?.title || "";

const hasIssuedGift = (gift) => {
	const giftId = gift?.gift_id;
	const didNotIssue = gift?.is_did_not_issue === true || gift?.return_status === "did_not_issue";
	return !didNotIssue && giftId != null && String(giftId).trim() !== "";
};

const buildInitialGiftReturns = (gifts) =>
	gifts.reduce((acc, gift, index) => {
		const key = String(gift?.id ?? gift?.gift_id ?? index);
		acc[key] = hasIssuedGift(gift) ? "returned" : "did_not_issue";
		return acc;
	}, {});

const CreditGiftReturnStep = ({
	detail,
	backStep,
	nextStep,
	onChange,
}) => {
	const gifts = useMemo(() => (Array.isArray(detail?.gifts) ? detail.gifts : []), [detail]);
	const maxRefundAmount = parseAmount(
		detail?.max_refundable_amount
	);
	const paymentRefundableAmount = parseAmount(
		detail?.paid_amount ?? 0	);
	
	const [refundAmount, setRefundAmount] = useState(formatAmount(maxRefundAmount));
	const [giftReturns, setGiftReturns] = useState(() => buildInitialGiftReturns(gifts));
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		setRefundAmount(formatAmount(maxRefundAmount));
		setGiftReturns(buildInitialGiftReturns(gifts));
		setErrorMessage("");
	}, [gifts, maxRefundAmount]);

	useEffect(() => {
		onChange?.({
			refund_amount: parseAmount(refundAmount),
			max_refundable_amount: maxRefundAmount,
			gifts: gifts.map((gift, index) => {
				const key = String(gift?.id ?? gift?.gift_id ?? index);
				return {
					id: gift?.id ?? null,
					gift_id: gift?.gift_id ?? null,
					gift_category_id: gift?.gift_category_id ?? null,
					voucher_serial_number: gift?.voucher_serial_number ?? "",
					return_status: giftReturns[key] || (hasIssuedGift(gift) ? "returned" : "did_not_issue"),
				};
			}),
		});
	}, [giftReturns, gifts, maxRefundAmount, onChange, refundAmount]);

	const handleAmountChange = (event) => {
		const value = event.target.value;
		if (!/^\d*\.?\d{0,2}$/.test(value)) return;
		setRefundAmount(value);
		setErrorMessage("");
	};

	const handleNext = () => {
		const amount = parseAmount(refundAmount);
		if (amount <= 0) {
			setErrorMessage("Enter a refund amount.");
			return;
		}
		if (amount > maxRefundAmount) {
			setErrorMessage(`Refund amount cannot exceed $${formatAmount(maxRefundAmount)}.`);
			return;
		}
		setErrorMessage("");
		nextStep?.();
	};

	return (
		<div className={`${styles.creditGiftReturnStep} popup`}>
			<div className="header">
				<h2>Credits and Gifts Return</h2>
			</div>

			<div className="body">
				{gifts.map((gift, index) => {
					const key = String(gift?.id ?? gift?.gift_id ?? index);
					const serial = String(gift?.voucher_serial_number || "").trim();
					const issued = hasIssuedGift(gift);
					const selected = giftReturns[key] || (issued ? "returned" : "did_not_issue");
					return (
						<div key={key} className={styles.giftBlock}>
							<div className={styles.giftTitle}>
								{getGiftTitle(gift, index)}: {getGiftName(gift)}
							</div>
							{issued ? (
								<>
									<label className={styles.radioRow}>
										<input
											type="radio"
											name={`gift-return-${key}`}
											checked={selected === "returned"}
											onChange={() => setGiftReturns((prev) => ({ ...prev, [key]: "returned" }))}
										/>
										<span>Returned{serial ? ` (${serial})` : ""}</span>
									</label>
									<label className={styles.radioRow}>
										<input
											type="radio"
											name={`gift-return-${key}`}
											checked={selected === "unreturned"}
											onChange={() => setGiftReturns((prev) => ({ ...prev, [key]: "unreturned" }))}
										/>
										<span>Unreturned</span>
									</label>
								</>
							) : (
								<label className={styles.radioRow}>
									<input
										type="radio"
										name={`gift-return-${key}`}
										checked
										readOnly
									/>
									<span>Did not issue</span>
								</label>
							)}
						</div>
					);
				})}

				<label className={styles.amountLabel} htmlFor="membership-refund-amount">
					Enter Refund Amount ($)*
				</label>
				<input
					id="membership-refund-amount"
					className={styles.amountInput}
					value={refundAmount}
					onChange={handleAmountChange}
					onBlur={() => setRefundAmount(formatAmount(refundAmount))}
				/>
				<div className={styles.maxText}>Max Refundable : ${formatAmount(maxRefundAmount)}</div>
				{/* <div className={styles.maxText}>Payment Refundable : ${formatAmount(paymentRefundableAmount)}</div> */}
				{errorMessage ? <div className={styles.errorMessage}>{errorMessage}</div> : null}
			</div>

			<div className="footer">
				<BorderedButton text="Back" onClick={backStep} />
				<PrimaryButton text="Next" onClick={handleNext} />
			</div>
		</div>
	);
};

export default CreditGiftReturnStep;
